import { useCallback, useEffect, useState } from 'react'
import { useCatalog } from '../../context/CatalogContext'
import {
  createScreen,
  listScreens,
  newPairCode,
  pairUrl,
  removeScreen,
  updateScreen,
  type KitchenScreen,
  type PairCode,
  type ScreenKind,
} from '../../services/screens'
import { stationNames } from '../../utils/stations'
import { QrCode } from '../QrCode'
import { CheckIcon, PencilIcon, PlusIcon, TrashIcon, XIcon } from '../icons'
import './KitchenScreensPanel.css'

/**
 * Ayarlar > Mutfak ekranları (kullanıcı, 11 Eki 2026).
 *
 * Mutfak ekranı kişi değil, cihaz: patron ekler, tablet QR, 6 haneli kod ya
 * da paylaşılan bağlantıyla bağlanır. Kullanıcı adı yok, kurumlar arası ad
 * çakışması yok.
 */
/** Mutfak ekranı ile kiosk aynı panel (30 Eki 2026); yazılar türe göre. */
const TEXT = {
  kitchen: {
    lead: 'Mutfak ekranı, bir tezgahın siparişlerini gösteren tablettir. Ekranı ekleyin, sonra tableti karekodla, 6 haneli kodla ya da paylaştığınız bağlantıyla bağlayın. Tablet bir kez bağlanır, bir daha giriş istemez.',
    empty: 'Henüz mutfak ekranı yok.',
    add: 'Ekran ekle',
    addTitle: 'Mutfak ekranı ekle',
    editTitle: 'Ekranı düzenle',
    placeholder: 'Izgara ekranı',
    share: 'Adissoo mutfak ekranı',
    opened: 'Mutfak ekranı tablette açıldı. Bu pencereyi kapatabilirsiniz.',
  },
  kiosk: {
    lead: 'Kiosk, müşterinin siparişini kendisi verdiği tablettir. Sipariş kasaya "ödeme bekliyor" olarak düşer, sıra numarası alır; kasada ödenince mutfağa gider. Kioskı ekleyin, sonra tableti karekodla ya da 6 haneli kodla bağlayın.',
    empty: 'Henüz kiosk yok.',
    add: 'Kiosk ekle',
    addTitle: 'Kiosk ekle',
    editTitle: 'Kioskı düzenle',
    placeholder: 'Giriş kioskı',
    share: 'Adissoo kiosk',
    opened: 'Kiosk tablette açıldı. Bu pencereyi kapatabilirsiniz.',
  },
} as const

export function KitchenScreensPanel({ tenantId, kind = 'kitchen' }: { tenantId: string; kind?: ScreenKind }) {
  const T = TEXT[kind]
  const { categories, products } = useCatalog()
  const [screens, setScreens] = useState<KitchenScreen[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Ekleme ya da düzenleme penceresi. */
  const [form, setForm] = useState<{ id?: string; name: string; stations: string[] } | null>(null)
  const [busy, setBusy] = useState(false)
  /** Bağlama kodu penceresi. */
  const [pairing, setPairing] = useState<{ screen: KitchenScreen; code: PairCode | null; openedAt: number } | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<KitchenScreen | null>(null)

  /** Menüdeki tezgahlar (kategorilerden ve ürüne özel olanlar). */
  const allStations = stationNames(categories, products)

  const reload = useCallback(async () => {
    try {
      setScreens(await listScreens(tenantId, kind))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    void reload()
  }, [reload])

  // Kod penceresi açıkken tablet bağlandı mı diye bak.
  useEffect(() => {
    if (!pairing) return
    const t = window.setInterval(() => void reload(), 4000)
    return () => window.clearInterval(t)
  }, [pairing, reload])

  const onSave = async () => {
    if (!form || !form.name.trim()) return
    setBusy(true)
    setError(null)
    try {
      if (form.id) {
        await updateScreen(tenantId, form.id, form.name.trim(), form.stations)
        setForm(null)
        await reload()
      } else {
        const r = await createScreen(tenantId, form.name.trim(), form.stations, kind)
        setForm(null)
        await reload()
        setPairing({
          screen: { id: r.screenId, name: form.name.trim(), stations: form.stations, paired: false },
          code: { code: r.code, expiresAt: r.expiresAt },
          openedAt: Date.now(),
        })
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const openPairing = async (s: KitchenScreen) => {
    setPairing({ screen: s, code: null, openedAt: Date.now() })
    try {
      const code = await newPairCode(tenantId, s.id)
      setPairing((p) => (p && p.screen.id === s.id ? { ...p, code } : p))
    } catch (e) {
      setError((e as Error).message)
      setPairing(null)
    }
  }

  const onRemove = async (s: KitchenScreen) => {
    setBusy(true)
    try {
      await removeScreen(tenantId, s.id)
      setConfirmRemove(null)
      await reload()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const stationLabel = (list: string[]) => (list.length ? list.join(', ') : 'Bütün tezgahlar')
  const live = pairing ? screens.find((s) => s.id === pairing.screen.id) : undefined
  const justPaired = !!(live?.pairedAt && pairing && live.pairedAt >= pairing.openedAt - 2000)

  return (
    <div className="kscr">
      <p className="kscr__lead">{T.lead}</p>

      {error && <p className="kscr__error">{error}</p>}

      {loading ? null : screens.length === 0 ? (
        <p className="kscr__empty">{T.empty}</p>
      ) : (
        <ul className="kscr__list">
          {screens.map((s) => (
            <li key={s.id}>
              <div className="kscr__info">
                <b>{s.name}</b>
                {kind === 'kitchen' && <span>{stationLabel(s.stations)}</span>}
              </div>
              <span className={`kscr__state ${s.paired ? 'is-on' : ''}`}>{s.paired ? 'Bağlı' : 'Bağlanmadı'}</span>
              <div className="kscr__actions">
                <button type="button" className="btn btn--outline" onClick={() => void openPairing(s)}>
                  {s.paired ? 'Yeni tablet bağla' : 'Bağla'}
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  title="Düzenle"
                  aria-label={`${s.name} ekranını düzenle`}
                  onClick={() => setForm({ id: s.id, name: s.name, stations: s.stations })}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  className="icon-btn is-danger"
                  title="Kaldır"
                  aria-label={`${s.name} ekranını kaldır`}
                  onClick={() => setConfirmRemove(s)}
                >
                  <TrashIcon />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="btn btn--primary kscr__add" onClick={() => setForm({ name: '', stations: [] })}>
        <PlusIcon />
        {T.add}
      </button>

      {form && (
        <div className="dlg" role="dialog" aria-modal="true" aria-label={form.id ? T.editTitle : T.addTitle}>
          <form
            className="dlg__card"
            onSubmit={(e) => {
              e.preventDefault()
              void onSave()
            }}
          >
            <div className="kscr__dhead">
              <h2>{form.id ? T.editTitle : T.addTitle}</h2>
              <button type="button" className="kscr__x" onClick={() => setForm(null)} aria-label="Kapat">
                <XIcon />
              </button>
            </div>
            <label className="dlg__field">
              Ekranın adı
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={T.placeholder}
                maxLength={40}
                required
              />
            </label>
            {kind === 'kitchen' && (
            <>
            {/* Birden çok tezgah seçilebilir; hiçbiri seçilmezse bütün tezgahlar. */}
            <div className="dlg__field">
              Tezgahları
              <div className="kscr__chips" role="group" aria-label="Tezgahları">
                <button
                  type="button"
                  className={form.stations.length === 0 ? 'is-on' : ''}
                  aria-pressed={form.stations.length === 0}
                  onClick={() => setForm({ ...form, stations: [] })}
                >
                  {form.stations.length === 0 && <CheckIcon />}
                  Bütün tezgahlar
                </button>
                {allStations.map((st) => {
                  const on = form.stations.includes(st)
                  return (
                    <button
                      key={st}
                      type="button"
                      className={on ? 'is-on' : ''}
                      aria-pressed={on}
                      onClick={() =>
                        setForm({
                          ...form,
                          stations: on ? form.stations.filter((x) => x !== st) : [...form.stations, st],
                        })
                      }
                    >
                      {on && <CheckIcon />}
                      {st}
                    </button>
                  )
                })}
              </div>
              <small className="kscr__hint">
                Tablet yalnız seçilen tezgahların fişlerini gösterir; tezgahı tabletten değiştirilemez.
              </small>
            </div>
            </>
            )}
            <div className="dlg__buttons">
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy || !form.name.trim()}>
                {busy ? 'Kaydediliyor…' : form.id ? 'Kaydet' : 'Ekle ve bağla'}
              </button>
            </div>
          </form>
        </div>
      )}

      {pairing && (
        <PairDialog
          kind={kind}
          name={pairing.screen.name}
          code={pairing.code}
          paired={justPaired}
          onNewCode={() => void openPairing(pairing.screen)}
          onClose={() => setPairing(null)}
        />
      )}

      {confirmRemove && (
        <div className="dlg" role="dialog" aria-modal="true" aria-label="Ekranı kaldır">
          <div className="dlg__card dlg__card--ask">
            <h2>{confirmRemove.name} kaldırılsın mı?</h2>
            <p className="dlg__empty">
              Bağlı tablet ekrandan çıkar. Aynı tableti yeniden kullanmak için yeni ekran ekleyip
              bağlamanız gerekir. Siparişler ve mutfak fişleri silinmez.
            </p>
            <div className="dlg__buttons dlg__buttons--even">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmRemove(null)}>
                Vazgeç
              </button>
              <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void onRemove(confirmRemove)}>
                Kaldır
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PairDialog({
  kind,
  name,
  code,
  paired,
  onNewCode,
  onClose,
}: {
  kind: ScreenKind
  name: string
  code: PairCode | null
  paired: boolean
  onNewCode: () => void
  onClose: () => void
}) {
  const [now, setNow] = useState(Date.now())
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  const left = code ? Math.max(0, Math.round((code.expiresAt - now) / 1000)) : 0
  const expired = !!code && left === 0
  const url = code ? pairUrl(code.code) : ''
  const pretty = code ? `${code.code.slice(0, 3)} ${code.code.slice(3)}` : ''

  const share = async () => {
    if (!code) return
    const text = `${TEXT[kind].share} (${name}): tablette bu bağlantıyı açın. Kod: ${pretty}`
    try {
      if (navigator.share) {
        await navigator.share({ title: TEXT[kind].share, text, url })
        return
      }
    } catch {
      /* paylaşım penceresi kapatıldı */
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, '_blank')
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label={`${name}: tableti bağla`}>
      <div className="dlg__card kpair">
        <div className="kscr__dhead">
          <div>
            <h2>Tableti bağlayın</h2>
            <p className="kpair__name">{name}</p>
          </div>
          <button type="button" className="kscr__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        </div>

        {paired ? (
          <div className="kpair__done" role="status">
            <CheckIcon />
            <strong>Tablet bağlandı</strong>
            <span>{TEXT[kind].opened}</span>
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Tamam
            </button>
          </div>
        ) : !code ? (
          <p className="kpair__wait">Kod hazırlanıyor…</p>
        ) : expired ? (
          <div className="kpair__expired">
            <strong>Kodun süresi doldu</strong>
            <button type="button" className="btn btn--primary" onClick={onNewCode}>
              Yeni kod al
            </button>
          </div>
        ) : (
          <>
            <div className="kpair__body">
              <QrCode value={url} size={210} label={`${name} ekranı bağlama karekodu`} />
              <div className="kpair__code">
                <span>Bağlama kodu</span>
                <strong>{pretty}</strong>
                <small>
                  {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} içinde geçerli, tek kullanımlık
                </small>
                <button type="button" className="btn btn--outline" onClick={() => void share()}>
                  {copied ? 'Bağlantı kopyalandı' : 'Bağlantıyı paylaş'}
                </button>
              </div>
            </div>

            <ol className="kpair__ways">
              <li>
                <b>Karekod:</b> tabletin kamerasıyla okutun.
              </li>
              <li>
                <b>Kod:</b> tablette <code>adissoo.com/ekran</code> adresini açıp kodu yazın.
              </li>
              <li>
                <b>Bağlantı:</b> "Bağlantıyı paylaş" ile tablete gönderip açın.
              </li>
            </ol>
            <p className="kpair__ipad">
              <b>iPad'de sıra önemli:</b> önce Adissoo'yu ana ekrana ekleyin, sonra ana ekrandan açıp kodu
              yazın. Tablette adım adım yönerge çıkar.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
